import { adaptFormItemResponseToFormFields } from '../core';
import {
  adaptAddressGroupResponse,
  adaptFieldsGroupRestrictionResponse,
  stampGuardedFieldError,
} from '../utilities';
import { ArrayFormField, FormField, IFormBase } from '@/forms/typings';
import { FormFieldsGroupResponseDTO, FormItemTypeDTO } from '@/api/base/DTOs';
import { ArrayFormFieldBuilder } from '@/forms/builders';

//? These are the only FormItemTypes that are supported as children of a nested FormFieldsGroup (Array of fields)
const UNSUPPORTED_FORM_FIELDS_GROUP_CHILDREN_FORM_ITEM_TYPES = [
  //? This FormItemType is for internal use of the API
  FormItemTypeDTO.HiddenField,
];

export const adaptFormFieldsGroupResponseToArrayFormField = (
  formFieldsGroupResponse: FormFieldsGroupResponseDTO,
  parentFieldId?: IFormBase['fieldId'],
): ArrayFormField => {
  // Filter out FormItemTypes that are not supported

  const filteredChildren = formFieldsGroupResponse.children.filter(
    child =>
      !UNSUPPORTED_FORM_FIELDS_GROUP_CHILDREN_FORM_ITEM_TYPES.includes(
        child.formItemType,
      ),
  );

  // Calculate hasAnyValue excluding hidden fields
  const hasAnyValue =
    formFieldsGroupResponse.notApplicable ||
    formFieldsGroupResponse.children.some(
      child =>
        !UNSUPPORTED_FORM_FIELDS_GROUP_CHILDREN_FORM_ITEM_TYPES.includes(
          child.formItemType,
        ) && child.hasAnyValue,
    );

  let arrayField = new ArrayFormFieldBuilder()
    .fieldId(formFieldsGroupResponse.fieldID)
    .name(formFieldsGroupResponse.fieldID)
    .title(formFieldsGroupResponse.title)
    .entryTitle(formFieldsGroupResponse.entryTitle ?? undefined)
    .hasAnyValue(hasAnyValue)
    .index(formFieldsGroupResponse.index)
    .maxItems(formFieldsGroupResponse.maxGroupCount)
    .minItems(formFieldsGroupResponse.minGroupCount)
    .questionIfOptional(formFieldsGroupResponse.questionIfOptional)
    .isRequired(formFieldsGroupResponse.isRequired)
    .isMultiple(formFieldsGroupResponse.isMultiple)
    .isEmpty(formFieldsGroupResponse.isEmpty)
    .clearsToEmpty(formFieldsGroupResponse.clearsToEmpty)
    .providerAdded(formFieldsGroupResponse.providerAdded)
    .allowRemove(formFieldsGroupResponse.allowRemove)
    .hasAnyValue(hasAnyValue)
    .notApplicable(formFieldsGroupResponse.notApplicable)
    .showNotApplicable(formFieldsGroupResponse.showNotApplicable)
    .parentFieldId(parentFieldId)
    .address(adaptAddressGroupResponse(formFieldsGroupResponse.address))
    .fields(
      filteredChildren.map(child =>
        adaptFormItemResponseToFormFields(
          child,
          formFieldsGroupResponse.fieldID,
        ),
      ) as Array<Exclude<FormField, ArrayFormField>>,
    );

  if (formFieldsGroupResponse.hint) {
    arrayField = arrayField.hint(formFieldsGroupResponse.hint);
  }

  if (formFieldsGroupResponse.hintImageUrl) {
    arrayField = arrayField.hintImageUrl(formFieldsGroupResponse.hintImageUrl);
  }

  if (formFieldsGroupResponse.isCollapsible) {
    arrayField = arrayField.isCollapsible(
      formFieldsGroupResponse.isCollapsible,
    );
  }

  const restriction = adaptFieldsGroupRestrictionResponse(
    formFieldsGroupResponse.restriction,
  );
  if (restriction) {
    arrayField = arrayField.restriction(restriction);
  }

  return stampGuardedFieldError(arrayField.build());
};
